<!-- Quill によるメンション機能を持つ入力欄コンポーネント -->
<script setup lang="ts">
  import Quill, { Delta, Op } from "quill/core";
  import "quill-mention/autoregister";

  export interface MentionUser {
    id: string;
    name: string;
    image: string;
    isOwner: boolean;
  }

  interface Props {
    placeholder: string;
    maxTextLength: number;
    mentionPlaceholder: string;
    getMentionUsers: (searchTerm: string) => Promise<MentionUser[]>;
    getMentionUserName?: (userId: string) => Promise<string | null>;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "change", message: string, mentionUserIds: string[], contents: any): void;
    (event: "done"): void;
    (event: "focus"): void;
    (event: "blur"): void;
    (event: "hideMentionList"): void;
  }
  const emit = defineEmits<Emits>();

  const textInput = ref<null | HTMLDivElement>(null);

  // ref: https://github.com/quill-mention/quill-mention/blob/dd5e67c51217106d26f52a2b539fd197041e49f8/src/mention.ts#L143-L152
  type RendorListFunc = (
    matches: {
      id: string;
      value: string;
      [key: string]: string | undefined;
    }[],
  ) => void;

  // Quill オブジェクトを保持
  // Ref 型にすると動作が不安定になるので let で保持することにした
  let quill: Quill | null = null;

  // メンション候補
  const mentionUsers = ref<MentionUser[]>([]);

  // PC の場合は Enter で改行、Enter + Shift で送信
  // SP の場合はいずれも改行
  const isSp = isSpDisplayRef();
  const onEnterKey = () => {
    // デフォルトの改行入力
    return true;
  };
  const onShiftEnterKey = () => {
    if (isSp.value) {
      // デフォルトの改行入力
      return true;
    } else {
      // 送信は上位コンポーネントで実行するため、ここでは emit するだけ
      emit("done");
      return false;
    }
  };
  const keyBindings = {
    enter: {
      key: "Enter",
      shiftKey: false,
      handler: onEnterKey,
    },
    shiftEnter: {
      key: "Enter",
      shiftKey: true,
      handler: onShiftEnterKey,
    },
  };

  // テキスト変更時の処理
  const onTextChange = (_delta: Delta, oldContents: Delta, _source: string) => {
    // 最大文字数を超えた場合はロールバック
    // Quill には最大文字数設定がないため、ここで制御する
    const text = quill!.getText();
    if (text.length > props.maxTextLength) {
      quill!.setContents(oldContents);
      return;
    }

    // メンション部分はプレースホルダーに置き換え、
    // メンション対象のユーザーIDリストとともに変更を通知
    const contents = quill!.getContents();
    let message = "";
    const mentionUserIds = new Array<string>();
    for (const o of contents.ops) {
      const insert = o.insert as any;
      if (typeof insert === "string") {
        message += insert;
      } else if (insert && "mention" in insert) {
        message += props.mentionPlaceholder;
        mentionUserIds.push(insert.mention.id);
      } else {
        consoleError("onTextChange: unexpected insert", insert);
      }
    }
    // Quill 仕様でメッセージ末尾に改行が付与されてしまうので、ここでトリムする
    emit("change", message.trim(), mentionUserIds, contents);
  };

  // メンション候補リストを取得して描画
  const sourceFunc = async (searchTerm: string, renderList: RendorListFunc) => {
    mentionUsers.value = await props.getMentionUsers(searchTerm);
    renderList(mentionUsers.value.map(u => ({ id: u.id, value: u.name })));
  };

  // メンション候補リストを消す
  const clearList = () => {
    if (quill) {
      // 非公開メソッドを呼び出してメンション候補リストを消す
      // https://github.com/quill-mention/quill-mention/blob/v6.0.2/src/mention.ts#L414
      (quill.getModule("mention") as any).hideMentionList();
      emit("hideMentionList");
    }
  };
  // ルーティング変更時（講座内ページへの移動時）にメンション候補リストを消す
  const route = useRoute();
  watch(() => route.fullPath, clearList);

  // 各メンション候補を描画
  const renderItemFunc = (item: any, _searchTerm: string) => {
    const wrapper = document.createElement("div");
    const img = document.createElement("img");
    const name = document.createElement("span");
    const user = mentionUsers.value.find(u => u.id === item.id) as MentionUser;
    img.src = user.image;
    img.width = 24;
    img.height = 24;
    img.style.marginRight = "10px";
    if (!user.isOwner) {
      img.style.borderRadius = "50%";
    }
    name.innerText = `@${item.value}`;
    name.style.verticalAlign = "top";
    wrapper.appendChild(img);
    wrapper.appendChild(name);
    return wrapper;
  };

  // Quill インスタンスを生成
  const createQuill = () => {
    const q = new Quill(textInput.value!, {
      placeholder: props.placeholder,
      formats: ["mention"],
      modules: {
        keyboard: {
          bindings: keyBindings,
        },
        mention: {
          allowedChars: /^.*$/,
          mentionDenotationChars: ["@"],
          minChars: 0,
          maxChars: 20, // ユーザー名は最大 20 文字なので

          // メンション候補リストの表示位置
          // モーダル上で使う場合含めると fixed が適切
          positioningStrategy: "fixed",

          // 下記 2 つセットでいれるとメンション前後にスペースが入る
          isolateCharacter: true,
          spaceAfterInsert: true,

          // メンション表示用コールバック
          source: sourceFunc,
          renderItem: renderItemFunc,
        },
      },
    });

    // テキスト変更イベントを拾う
    q.on("text-change", onTextChange);

    return q;
  };

  // マウント時に Quill インスタンスを生成
  onMounted(() => {
    quill = createQuill();
  });

  // アンマウント時（講座外ページへの移動時）にメンション候補リスト非表示および Quill インスタンスを破棄
  onUnmounted(() => {
    if (quill) {
      clearList();
      quill.off("text-change", onTextChange);
      quill = null;
    }
  });

  // ------------------------------------
  // 以下、expose 用関数群
  // ------------------------------------

  // メンションを挿入して候補表示
  const openMenu = () => {
    // 候補が確実に出るように手前にスペースを入れる
    (quill!.getModule("mention") as any).openMenu(" @");
  };
  // テキストのみデータをセット
  const setText = (text: string) => {
    quill!.setText(text);
  };
  // テキスト＋メンション（0個以上）データをセット
  const setTextAndMentions = async (text: string, mentionUserIds: string[]) => {
    if (!props.getMentionUserName) {
      consoleError("setTextAndMentions: props.getMentionUserName must be set");
      return;
    }
    const ops: Op[] = [];
    const sp = text.split(props.mentionPlaceholder);
    for (let i = 0; i < sp.length; i++) {
      ops.push({ insert: sp[i] });
      if (i < mentionUserIds.length) {
        const userName = (await props.getMentionUserName(mentionUserIds[i])) ?? "(unknown)";
        ops.push({
          insert: {
            mention: {
              denotationChar: "@",
              id: mentionUserIds[i],
              value: userName,
            },
          },
        });
      }
    }
    quill!.setContents(new Delta(ops));
  };
  // テキスト＋メンション（0個以上）データを contents としてセット
  const setContents = (contents: any) => {
    if (contents.ops.length === 1 && contents.ops[0].insert.trim() === "") {
      // contents.ops がひとつで空文字列相当の場合に setContents すると何故かフリーズするので、
      // その場合は "" でsetText する
      quill!.setText("");
    } else {
      quill!.setContents(contents);
    }
  };
  const focus = () => {
    quill!.focus();
    emit("focus");
  };
  const blur = () => {
    quill!.blur();
    emit("blur");
  };

  defineExpose({
    openMenu,
    setText,
    setTextAndMentions,
    setContents,
    focus,
    blur,
    onEnterKey, // VisibleForTesting
    onShiftEnterKey, // VisibleForTesting
  });
</script>

<template>
  <div ref="textInput" class="text-input"></div>
</template>

<style lang="scss" scoped>
  .text-input {
    padding: 8px 16px;
    background-color: #666;
    border-radius: 24px;
    text-align: left;
    position: relative;

    max-height: min(60vh, 500px); /* 画面の60% or 500pxの小さい方 */
    overflow-y: scroll;
    // スクロールバーを非表示にする (以下の一式でクロスブラウザ対応)
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
</style>
<style lang="scss">
  @import "assets/media.scss";
  .ql-editor {
    outline: none; // 入力中の外枠を消す
    line-height: 1.7; // メンションが重ならないようやや広めに

    // メンション前後のスペース調整に必要
    // これがないとメンション挿入後にカーソル位置や日本語変換で不正な状態に陥ることあり
    // https://fincs.backlog.com/view/FINCS-3145
    white-space: pre-wrap;
  }
  .ql-editor.ql-blank::before {
    color: rgba(160, 160, 160, 1);
    content: attr(data-placeholder);
    pointer-events: none;
    position: absolute;
  }
  .ql-editor.ql-blank:focus::before {
    content: "";
  }
  .mention {
    background-color: #161616;
    border-radius: 10px;
    color: rgb(17, 156, 246);
    padding: 0 4px;
  }
  .ql-mention-list-container {
    background-color: #333;
    max-height: 80svh;
    z-index: 3000; // モーダルが 2400 なのでそれより上に表示できるように
    overflow-y: scroll;
    // スクロールバーを非表示にする (以下の一式でクロスブラウザ対応)
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  @include display_sp {
    .ql-mention-list-container {
      max-height: 40svh;
      bottom: 50px !important;
    }
  }
  .ql-mention-list-item {
    color: white;
    padding: 4px 8px;
    cursor: pointer;
    :hover {
      background-color: #444;
    }
  }
</style>
